// Meta's adapter onto the shared MultiTrendChart — the mirror of
// googleAds/GoogleAdsTrend's `buildGoogleMultiTrend`. PURE.
//
// ⚠️ IT REUSES `buildTrendSeries`, deliberately. That is the ONE shaper every
// chart on the Meta page already reads (the hero mini-charts and the big
// over-time chart), so the multi-metric chart inherits its rules for free rather
// than acquiring a second set:
//
//   · money ratios recompute from each bucket's SUMMED numerator / denominator,
//     so a weekly cost-per-lead is the week's spend ÷ the week's leads, never
//     the mean of seven daily figures;
//   · every calendar bucket in the range gets a slot, so a gap stays visible in
//     position rather than being collapsed;
//   · an empty RATIO bucket is `null` (a break in the line) while an empty
//     ADDITIVE one is a real 0 — the distinction `MetricKind` exists for.
//
// ⚠️ Money is already scoped to ONE display currency before this runs (the page
// scopes the whole summary through `scopeSummaryToCurrency`), so unlike Google
// there is no cross-currency sum here at all — the plotted value and the
// formatted tooltip figure are the same number in the same currency.

import type { ReportingSummary } from '../../types/reporting';
import type { TrendGrouping } from './chartDate';
import type { MultiTrendData, MultiTrendSeries } from './MultiTrendChart';
import { buildTrendSeries, formatMetricValue, resolveTrendMetric } from './trendSeries';

/**
 * Shape the (currency-scoped) summary into multi-series chart data.
 *
 * ⚠️ Series are indexed BY BUCKET rather than by position. Every series spans the
 * same range at the same grouping, so in practice the rows align — but keying on
 * the bucket id means a future shaper change can't silently slide one metric's
 * line a day left against the others, which is the kind of wrong that looks
 * completely plausible on screen.
 */
export function buildMetaMultiTrend(
  summary: ReportingSummary | null,
  metricKeys: readonly string[],
  grouping: TrendGrouping,
): MultiTrendData {
  if (!summary || metricKeys.length === 0) {
    return { buckets: [], grouping, series: [] };
  }

  const built = metricKeys.map((key) => ({
    key,
    metric: resolveTrendMetric(key),
    data: buildTrendSeries(summary, key, grouping),
  }));

  // The union of every series' buckets, chronological. Bucket ids (ISO dates and
  // 'YYYY-MM') sort chronologically as strings — the same property the shaper's
  // own domain fill relies on.
  const buckets = [...new Set(built.flatMap((b) => b.data.rows.map((r) => r.bucket)))].sort(
    (a, b) => a.localeCompare(b),
  );

  const series: MultiTrendSeries[] = built.map((b) => {
    const byBucket = new Map(b.data.rows.map((r) => [r.bucket, r.value]));
    return {
      key: b.key,
      label: b.metric.label,
      points: buckets.map((bucket) => {
        const value = byBucket.has(bucket) ? (byBucket.get(bucket) ?? null) : null;
        return {
          value,
          // An unmeasured bucket is an em dash in the tooltip, never a "0" —
          // the same distinction the KPI tiles make.
          formatted:
            value === null ? '—' : formatMetricValue(b.data.format, value, b.data.currency),
        };
      }),
    };
  });

  return { buckets, grouping, series };
}
